import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Listing, SearchSummary } from '../types';
import { searchLabel } from '../format';
import ListingCard from './ListingCard';
import ListingDetail from './ListingDetail';

const ALL = 'all';

type ListingsApi = {
  fetchListings(searchId: string, limit?: number): Promise<Listing[]>;
};

type Props = {
  api: ListingsApi;
  searches: SearchSummary[];
  initialSearchId?: string;
  onNewSearch: () => void;
};

export function mergeListings(lists: Listing[][]): Listing[] {
  const byId = new Map<string, Listing>();
  lists.flat().forEach(listing => {
    if (!byId.has(listing.listingId)) byId.set(listing.listingId, listing);
  });
  return Array.from(byId.values())
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
}

export default function Feed({ api, searches, initialSearchId, onNewSearch }: Props) {
  const [filter, setFilter] = useState(initialSearchId ?? ALL);
  const [listingsBySearch, setListingsBySearch] = useState<Record<string, Listing[]> | null>(null);
  const [failed, setFailed] = useState(false);
  const [detail, setDetail] = useState<Listing | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all(searches.map(search => api.fetchListings(search.id).then(listings => [search.id, listings] as const)))
      .then(entries => {
        if (!cancelled) setListingsBySearch(Object.fromEntries(entries));
      })
      .catch(err => {
        console.error('Failed to load listings', err);
        if (!cancelled) setFailed(true);
      });
    return () => { cancelled = true; };
  }, [api, searches]);

  const visible = useMemo(() => {
    if (!listingsBySearch) return [];
    if (filter === ALL) return mergeListings(Object.values(listingsBySearch));
    return mergeListings([listingsBySearch[filter] ?? []]);
  }, [listingsBySearch, filter]);

  const closeDetail = useCallback(() => setDetail(null), []);

  if (detail) {
    return <ListingDetail listing={detail} onBack={closeDetail} />;
  }

  const filters = [{ id: ALL, label: 'All' }, ...searches.map(search => ({ id: search.id, label: searchLabel(search) }))];

  return (
    <>
      <header className="ma-header">
        <span className="ma-brand">HomeScout</span>
        <button type="button" className="ma-link-button" onClick={onNewSearch}>New search</button>
      </header>
      <main className="ma-body">
        {searches.length > 1 && (
          <div className="ma-chips ma-chips--scroll" style={{ marginBottom: 16 }}>
            {filters.map(f => (
              <button
                key={f.id}
                type="button"
                className="ma-chip ma-chip--dark"
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        {failed && <p className="ma-empty">Couldn't load flats. Please try again later.</p>}
        {!failed && !listingsBySearch && <p className="ma-empty">Loading flats…</p>}
        {!failed && listingsBySearch && visible.length === 0 && (
          <p className="ma-empty">No flats for this search yet. We'll message you as soon as one appears.</p>
        )}
        {visible.map(listing => (
          <ListingCard key={listing.listingId} listing={listing} onOpen={setDetail} />
        ))}
      </main>
    </>
  );
}
